import { Component } from 'react';
import { Button, Col, Input, Row, Select, Tooltip, Switch } from 'antd';
import { DeleteOutlined, SettingOutlined, PlusOutlined } from '@ant-design/icons';
import clone from 'lodash/clone';
import Widgets from '@src/packages/pro-component/widget';
import OrderableList from './OrderableList';
import ArrayEditor from './ArrayEditor';
import AffixMapEditor from './AffixMapEditor';
import ColorPicker from '@src/containers/ColorPicker';
import { DATA_TYPES } from '@src/constants/constants';
import { DATA_TYPE, GRID_EDITOR_FIELD } from '@src/constants/enums';
import { IGridSetting } from '@src/routes/default/pageManager/PageEditor';
import GridSetting from '../settings/GridSetting';
import * as detailTemplateArray from '../layouts/detailTemplate';
export const DEFAULT_VIEW_DETAIL = 'DetailCtrl';

interface GridEditorProps {
  name?: string;
  data?: any;
  apis?: any;
  settings: IGridSetting;
  onChange?: (val: any) => void;
  onSettingChange: (settingGrid: IGridSetting) => void;
}

export interface IGridEditorColumn {
  name: string;
  nameLa: string;
  nameEn: string;
  translations?: { language: string; name: string }[];
  field: string;
  type: DATA_TYPE & 'integer';
  fieldType?: string;
  width?: number;
  /**
   * @property {color}
   * @description thuoc tinh mau cho modeSelectApi chua trien khai
   */
  color?: string;
  enumable?: string;
  /** Hiển thị enum: badge (mặc định) hoặc tag */
  enumDisplayAs?: 'badge' | 'tag';
  items?: Array<any>;
  modelSelect?: string;
  modelSelectApi?: string;
  display?: string;
  filterable?: boolean;
  bindButton?: string;
  filterRange?: string;
  sorter?: boolean;
  hideInTable?: boolean;
  hideInSetting?: boolean;
  // hideInSearch,
  filters?: boolean; // lọc trên cột
  fixed?: 'left' | 'right' | 'none';
  copyable?: boolean;
  ellipsis?: boolean;
  /** Text trước giá trị cột */
  prefix?: string;
  /** Text sau giá trị cột */
  suffix?: string;
  /** Màu khi dùng prefix cố định */
  prefixColor?: string;
  /** Màu khi dùng suffix cố định */
  suffixColor?: string;
  /** Cột phụ thuộc để chọn prefix (vd: direction) */
  prefixByField?: string;
  /** Map value cột phụ thuộc → text/color prefix */
  prefixItems?: Array<{ value: string; text: string; color?: string }>;
  /** Cột phụ thuộc để chọn suffix */
  suffixByField?: string;
  /** Map value cột phụ thuộc → text/color suffix */
  suffixItems?: Array<{ value: string; text: string; color?: string }>;
  /** Gộp nhiều field vào 1 cột; mỗi phần tử có type/display riêng */
  wrapFields?: Array<{
    field: string;
    label?: string;
    type?: DATA_TYPE | string;
    display?: string;
    enumable?: boolean;
    enumDisplayAs?: 'badge' | 'tag';
    items?: Array<any>;
    modelSelect?: boolean;
    modelSelectApi?: string;
    /** Hiện ô lọc trên form search (cột wrap vẫn ẩn search) */
    filterable?: boolean;
    /** Ẩn label khi hiển thị trên table (filter vẫn dùng label) */
    hideLabelInTable?: boolean;
  }>;
  /** Phân cách khi wrapLayout = inline (mặc định " · ") */
  wrapSeparator?: string;
  /** Cách hiển thị: ngang hoặc xuống dòng */
  wrapLayout?: 'inline' | 'stack';
  menuButton?: boolean;
  menuButtonConditon?: number;
  accurate?: string;
  // nhúng chi tiết
  viewDetail?: boolean;
  // chi tiet template
  viewDetailCtrl?: string;
}

export const COLUMN_ACTIONS_FIELD = '--ACTIONS--';
export const COLUMN_ACTIONS_NAME = 'Tác vụ';
export const COLUMN_ACTIONS_NAME_LA = 'ຕັ້ງຄ່າ';
export const COLUMN_ACTIONS_NAME_EN = 'Action';
export const DEFAULT_MENU_BUTTONS = 4;

export interface GridEditorState {
  data: any;
  apis: any;
  currentItem: any;
  currentIndex: any;
  error: any;
  pIndex?: any;
  visibleSetting: boolean;
}

const populateData = (data: Array<any>) => {
  const dataWithActionCol = clone(data || []);
  if (
    dataWithActionCol.length > 0 &&
    !dataWithActionCol.find((col: any) => col.field === COLUMN_ACTIONS_FIELD)
  ) {
    dataWithActionCol.push({
      name: COLUMN_ACTIONS_NAME,
      nameLa: COLUMN_ACTIONS_NAME_LA,
      nameEn: COLUMN_ACTIONS_NAME_EN,
      field: COLUMN_ACTIONS_FIELD,
    });
  }
  return dataWithActionCol;
};
class GridEditor extends Component<GridEditorProps, GridEditorState> {
  constructor(props: any) {
    super(props);
    this.state = {
      data: populateData(props.data),
      apis: this.props.apis || [],
      currentItem: null,
      currentIndex: 0,
      error: null,
      visibleSetting: false,
    };
  }

  static getDerivedStateFromProps(
    nextProps: GridEditorProps,
    prevState: GridEditorState
  ) {
    if (nextProps.data !== prevState.data) {
      return { data: populateData(nextProps.data) };
    }
    if (nextProps.apis !== prevState.apis) {
      return { apis: nextProps.apis };
    } else return null;
  }

  onPropertyClick(property: any) {
    this.setState({ pIndex: property });
  }

  onChange(dt: IGridEditorColumn[]) {
    if (this.props.onChange) {
      this.props.onChange(dt);
    }
  }

  toogleSetting = () => {
    this.setState({ visibleSetting: !this.state.visibleSetting });
  };

  addItem = () => {
    const dt = this.state.data.splice(0);
    dt.push({});
    this.onChange(dt);
  };

  onItemDataChanged = (name: GRID_EDITOR_FIELD, val: any) => {
    const dt = this.state.data.splice(0);
    dt[this.state.currentIndex][name] = val;
    this.onChange(dt);
  };

  updateWrapField = (index: number, patch: Record<string, any>) => {
    const currentItem = this.state.data[this.state.currentIndex] || {};
    const next = [...(currentItem[GRID_EDITOR_FIELD.WRAP_FIELDS] || [])];
    next[index] = { ...next[index], ...patch };
    this.onItemDataChanged(GRID_EDITOR_FIELD.WRAP_FIELDS, next);
  };

  onSettingChange = (name: any, val: any) => {
    const settings: any = clone(this.props.settings);
    settings[name] = val;
    if (this.props.onSettingChange) {
      this.props.onSettingChange(settings);
    }
  };

  deleteProperty = () => {
    const dt = this.state.data.splice(0);
    dt.splice(this.state.currentIndex, 1);
    let currentIndex = this.state.currentIndex;
    currentIndex--;
    if (currentIndex < 0) currentIndex = 0;
    this.setState({ currentIndex });
    this.onChange(dt);
  };

  chooseDisplays = (type: DATA_TYPE) => {
    switch (type) {
      case DATA_TYPE.DATE:
        return ['date', 'dateRange', 'dateTimeRange', 'dateTime', 'time'];
      case DATA_TYPE.NUMBER:
        return [
          'numberOnly',
          'digit',
          'money',
          'progress',
          'percent',
          'index',
          'indexBorder',
        ];
      case DATA_TYPE.BOOLEAN:
        return ['switch', 'radio', 'radioGroup'];
      case DATA_TYPE.STRING:
        return ['text', 'textarea', 'avatar', 'code', 'image'];
      case DATA_TYPE.ARRAY_IDS:
        return ['arrayIds'];
      default:
        return ['option'];
    }
  };

  renderDisplay = ({
    type,
    currentItem,
    rowStyles = {},
    labelCols = {},
    inputCols = {},
  }: {
    // display: ProColumnsValueType
    type: DATA_TYPE;
    currentItem: Record<string, any>;
    rowStyles?: Record<string, any>;
    labelCols?: Record<string, any>;
    inputCols?: Record<string, any>;
  }) => {
    const valueTypes = this.chooseDisplays(type);
    return (
      <Row
        {...rowStyles}
        style={{ marginLeft: 0, marginRight: 0 }}
        align='middle'
        justify='end'
      >
        <Col {...labelCols}>
          <label>Hiển thị</label>
        </Col>
        <Col {...inputCols}>
          <Select
            style={{ minWidth: '150px' }}
            value={currentItem.display || valueTypes[0]}
            onChange={(e) => {
              this.onItemDataChanged(GRID_EDITOR_FIELD.DISPLAY, e);
            }}
          >
            {valueTypes.map((d, index) => (
              <Select.Option key={index} value={d}>
                {d}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
    );
  };

  renderActionColumn = ({
    currentItem,
    rowStyles = {},
    labelCols = {},
    inputCols = {},
  }: {
    currentItem: IGridEditorColumn;
    rowStyles?: Record<string, any>;
    labelCols?: Record<string, any>;
    inputCols?: Record<string, any>;
  }) => {
    return (
      <>
        <Row {...rowStyles} className='gx-mt-2' align='middle'>
          <Col {...labelCols}>
            <label>Tên</label>
          </Col>
          <Col {...inputCols}>
            <Input
              value={currentItem.name || ''}
              type='text'
              required
              onChange={(e) => {
                this.onItemDataChanged(GRID_EDITOR_FIELD.NAME, e.target.value);
              }}
            />
          </Col>
        </Row>
        <Row {...rowStyles} className='gx-mt-2' align='middle'>
          <Col {...labelCols}>
            <label>Trường dữ liệu</label>
          </Col>
          <Col {...inputCols}>
            <Input
              disabled
              value={currentItem.field || ''}
              type='text'
              required
              onChange={(e) => {
                this.onItemDataChanged(GRID_EDITOR_FIELD.FIELD, e.target.value);
              }}
            />
          </Col>
        </Row>
        <Row {...rowStyles} className='gx-mt-2' align='middle'>
          <Col {...labelCols}>
            <label>Thuộc tính</label>
          </Col>
          <Col {...inputCols}>
            <Row gutter={[16, 16]} align='middle'>
              <Col>
                <Widgets.CheckboxWidget
                  checkedChildren='Menu Button'
                  unCheckedChildren='Menu Button'
                  value={
                    typeof currentItem.menuButton == 'boolean'
                      ? currentItem.menuButton
                      : false
                  }
                  onChange={(val: any) => {
                    this.onItemDataChanged(GRID_EDITOR_FIELD.MENU_BUTTON, val);
                  }}
                />
              </Col>
              {currentItem.menuButton && (
                <Col>
                  {`Điều kiện: `}
                  <Input
                    placeholder='Điều kiện'
                    addonBefore='>='
                    value={
                      currentItem.menuButtonConditon || DEFAULT_MENU_BUTTONS
                    }
                    onChange={(e: any) => {
                      this.onItemDataChanged(
                        GRID_EDITOR_FIELD.MENU_BUTTON_CONDITION,
                        e.target.value
                      );
                    }}
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </>
    );
  };

  OrderableListSideBar = () => {
    return (
      <OrderableList
        name={'Cột'}
        items={this.state.data}
        renderItem={(item, index) => {
          return (
            <div
              className={`gx-editor-properties-item ${this.state.currentIndex === index ? 'active' : ''
                }`}
              onClick={() => this.setState({ currentIndex: index })}
            >
              <div className='gx-editor-properties-row'>
                <div className='gx-editor-avatar'>
                  <div className='gx-status-pos'>
                    {this.state.currentIndex === index ? (
                      <i className='icon icon-rendaring-calendar gx-pt-1' />
                    ) : (
                      <i className={`icon icon-circle gx-text-teal`} />
                    )}
                  </div>
                </div>
                <div className='gx-editor-orderable-col'>
                  <div className='h4 gx-name'>
                    {item.name || 'Chưa đặt tên'} {item.required ? '*' : ''}
                  </div>
                  <div className='gx-editor-info-des gx-text-truncate'>
                    {item.field}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
        activeIndex={this.state.currentIndex}
        onChange={(result) => {
          const dt = result.items.splice(0);
          this.onChange(dt);
          this.setState({ currentIndex: result.activeIndex });
        }}
        headerButtons={() => {
          return (
            <Button
              type='primary'
              className='gx-btn-block'
              onClick={() => {
                this.addItem();
              }}
            >
              {' '}
              Thêm trường{' '}
            </Button>
          );
        }}
      />
    );
  };

  Editor = (currentItem: IGridEditorColumn) => {
    const rowStyles: any = {
      gutter: [16, 16],
    };
    const labelCols = {
      sm: 24,
      md: 6,
    };
    const inputCols = {
      sm: 24,
      md: 18,
    };
    const isWrapEnabled =
      (currentItem?.[GRID_EDITOR_FIELD.WRAP_FIELDS]?.length || 0) > 0;

    return (
      <div className='gx-form-editor'>
        <div className='gx-form-editor-header'>
          <Row
            {...rowStyles}
            className='gx-mt-2'
            justify='space-between'
            align='middle'
          >
            <Col>
              <span>Thuộc tính </span>
            </Col>
            <Col>
              <Button
                type='primary'
                danger
                onClick={this.deleteProperty}
                icon={<DeleteOutlined />}
              >
                Xóa
              </Button>
            </Col>
          </Row>
        </div>
        <div className='gx-form-editor-content'>
          {currentItem.field === COLUMN_ACTIONS_FIELD ? (
            this.renderActionColumn({
              currentItem,
              rowStyles,
              labelCols,
              inputCols,
            })
          ) : (
            <>
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Tên</label>
                </Col>
                <Col {...inputCols}>
                  <Input
                    value={currentItem.name || ''}
                    type='text'
                    required
                    onChange={(e) => {
                      this.onItemDataChanged(
                        GRID_EDITOR_FIELD.NAME,
                        e.target.value
                      );
                    }}
                  />
                </Col>
              </Row>
              {/* <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Tên tiếng Lào</label>
                </Col>
                <Col {...inputCols}>
                  <Input
                    value={currentItem.nameLa || ''}
                    type='text'
                    required
                    onChange={(e) => {
                      this.onItemDataChanged(
                        GRID_EDITOR_FIELD.NAME_LA,
                        e.target.value
                      );
                    }}
                  />
                </Col>
              </Row>
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Tên tiếng Anh</label>
                </Col>
                <Col {...inputCols}>
                  <Input
                    value={currentItem.nameEn || ''}
                    type='text'
                    required
                    onChange={(e) => {
                      this.onItemDataChanged(
                        GRID_EDITOR_FIELD.NAME_EN,
                        e.target.value
                      );
                    }}
                  />
                </Col>
              </Row> */}
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>
                    {isWrapEnabled ? 'Key cột (ảo)' : 'Trường dữ liệu'}
                  </label>
                </Col>
                <Col {...inputCols}>
                  <Input
                    value={currentItem.field || ''}
                    type='text'
                    required
                    placeholder={
                      isWrapEnabled
                        ? 'vd: customerDisplay (key cột, không phải field API)'
                        : 'vd: name hoặc meta.customerName'
                    }
                    onChange={(e) => {
                      this.onItemDataChanged(
                        GRID_EDITOR_FIELD.FIELD,
                        e.target.value
                      );
                    }}
                  />
                </Col>
              </Row>
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Wrap nhiều field</label>
                </Col>
                <Col {...inputCols}>
                  <Switch
                    checked={
                      (currentItem[GRID_EDITOR_FIELD.WRAP_FIELDS]?.length ||
                        0) > 0
                    }
                    checkedChildren='Bật'
                    unCheckedChildren='Tắt'
                    onChange={(checked) => {
                      if (!checked) {
                        this.onItemDataChanged(
                          GRID_EDITOR_FIELD.WRAP_FIELDS,
                          undefined
                        );
                        return;
                      }
                      const dt = this.state.data.splice(0);
                      const idx = this.state.currentIndex;
                      dt[idx] = {
                        ...dt[idx],
                        [GRID_EDITOR_FIELD.WRAP_FIELDS]: [
                          { field: '', type: 'string', display: 'text' },
                        ],
                        [GRID_EDITOR_FIELD.WRAP_SEPARATOR]:
                          dt[idx][GRID_EDITOR_FIELD.WRAP_SEPARATOR] || ' · ',
                        [GRID_EDITOR_FIELD.WRAP_LAYOUT]:
                          dt[idx][GRID_EDITOR_FIELD.WRAP_LAYOUT] || 'inline',
                      };
                      this.onChange(dt);
                    }}
                  />
                </Col>
              </Row>
              {(currentItem[GRID_EDITOR_FIELD.WRAP_FIELDS]?.length || 0) > 0 ? (
                <>
                  <Row {...rowStyles} className='gx-mt-2' align='middle'>
                    <Col {...labelCols}>
                      <label>Kiểu wrap</label>
                    </Col>
                    <Col {...inputCols}>
                      <Select
                        style={{ minWidth: 160 }}
                        value={
                          currentItem[GRID_EDITOR_FIELD.WRAP_LAYOUT] ||
                          'inline'
                        }
                        onChange={(v) => {
                          this.onItemDataChanged(
                            GRID_EDITOR_FIELD.WRAP_LAYOUT,
                            v
                          );
                        }}
                      >
                        <Select.Option value='inline'>
                          Ngang (inline)
                        </Select.Option>
                        <Select.Option value='stack'>
                          Xuống dòng (stack)
                        </Select.Option>
                      </Select>
                    </Col>
                  </Row>
                  {(currentItem[GRID_EDITOR_FIELD.WRAP_LAYOUT] ||
                    'inline') === 'inline' ? (
                    <Row {...rowStyles} className='gx-mt-2' align='middle'>
                      <Col {...labelCols}>
                        <label>Phân cách</label>
                      </Col>
                      <Col {...inputCols}>
                        <Input
                          value={
                            currentItem[GRID_EDITOR_FIELD.WRAP_SEPARATOR] ??
                            ' · '
                          }
                          placeholder='vd:  ·  hoặc  - '
                          onChange={(e) => {
                            this.onItemDataChanged(
                              GRID_EDITOR_FIELD.WRAP_SEPARATOR,
                              e.target.value
                            );
                          }}
                        />
                      </Col>
                    </Row>
                  ) : null}
                  <Row {...rowStyles} className='gx-mt-2' align='top'>
                    <Col {...labelCols}>
                      <label>Danh sách field</label>
                    </Col>
                    <Col {...inputCols}>
                      {(
                        currentItem[GRID_EDITOR_FIELD.WRAP_FIELDS] || []
                      ).map((wf: any, wi: number) => {
                        const wfType = (wf?.type || 'string') as DATA_TYPE;
                        const wfDisplays = this.chooseDisplays(wfType);
                        return (
                          <div
                            key={wi}
                            style={{
                              marginBottom: 12,
                              padding: 10,
                              border: '1px solid #f0f0f0',
                              borderRadius: 8,
                              background: '#fafafa',
                            }}
                          >
                            <Row gutter={[8, 8]} align='middle'>
                              <Col xs={24} sm={14}>
                                <Input
                                  placeholder='field (vd: phone, meta.name)'
                                  value={wf?.field || ''}
                                  onChange={(e) => {
                                    this.updateWrapField(wi, {
                                      field: e.target.value,
                                    });
                                  }}
                                />
                              </Col>
                              <Col xs={20} sm={8}>
                                <Input
                                  placeholder='Label (tuỳ chọn)'
                                  value={wf?.label || ''}
                                  onChange={(e) => {
                                    this.updateWrapField(wi, {
                                      label: e.target.value,
                                    });
                                  }}
                                />
                              </Col>
                              <Col xs={4} sm={2}>
                                <Button
                                  type='text'
                                  danger
                                  icon={<DeleteOutlined />}
                                  disabled={
                                    (
                                      currentItem[
                                      GRID_EDITOR_FIELD.WRAP_FIELDS
                                      ] || []
                                    ).length <= 1
                                  }
                                  onClick={() => {
                                    const next = (
                                      currentItem[
                                      GRID_EDITOR_FIELD.WRAP_FIELDS
                                      ] || []
                                    ).filter(
                                      (_: any, idx: number) => idx !== wi
                                    );
                                    this.onItemDataChanged(
                                      GRID_EDITOR_FIELD.WRAP_FIELDS,
                                      next
                                    );
                                  }}
                                />
                              </Col>
                              <Col xs={12} sm={12}>
                                <Select
                                  style={{ width: '100%' }}
                                  placeholder='Kiểu dữ liệu'
                                  value={wfType}
                                  onChange={(v) => {
                                    const displays = this.chooseDisplays(
                                      v as DATA_TYPE
                                    );
                                    this.updateWrapField(wi, {
                                      type: v,
                                      display: displays[0],
                                    });
                                  }}
                                >
                                  {DATA_TYPES.map((d, index) => (
                                    <Select.Option key={index} value={d}>
                                      {d}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Col>
                              <Col xs={12} sm={12}>
                                <Select
                                  style={{ width: '100%' }}
                                  placeholder='Hiển thị'
                                  value={
                                    wf?.display || wfDisplays[0] || 'text'
                                  }
                                  onChange={(v) => {
                                    this.updateWrapField(wi, {
                                      display: v,
                                    });
                                  }}
                                >
                                  {wfDisplays.map((d, index) => (
                                    <Select.Option key={index} value={d}>
                                      {d}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Col>
                              <Col xs={24}>
                                <Switch
                                  size='small'
                                  checked={!!wf?.enumable}
                                  checkedChildren='Enum'
                                  unCheckedChildren='Enum'
                                  disabled={!!wf?.modelSelect}
                                  onChange={(checked) => {
                                    this.updateWrapField(wi, {
                                      enumable: checked,
                                      items: checked
                                        ? wf?.items || []
                                        : undefined,
                                      modelSelect: checked
                                        ? false
                                        : wf?.modelSelect,
                                    });
                                  }}
                                />
                                <Switch
                                  size='small'
                                  style={{ marginLeft: 8 }}
                                  checked={!!wf?.modelSelect}
                                  checkedChildren='CSDL'
                                  unCheckedChildren='CSDL'
                                  disabled={!!wf?.enumable}
                                  onChange={(checked) => {
                                    this.updateWrapField(wi, {
                                      modelSelect: checked,
                                      enumable: checked
                                        ? false
                                        : wf?.enumable,
                                      items: checked
                                        ? undefined
                                        : wf?.items,
                                    });
                                  }}
                                />
                                <Switch
                                  size='small'
                                  style={{ marginLeft: 8 }}
                                  checked={!!wf?.filterable}
                                  checkedChildren='Lọc'
                                  unCheckedChildren='Lọc'
                                  onChange={(checked) => {
                                    this.updateWrapField(wi, {
                                      filterable: checked,
                                    });
                                  }}
                                />
                                <Switch
                                  size='small'
                                  style={{ marginLeft: 8 }}
                                  checked={!!wf?.hideLabelInTable}
                                  checkedChildren='Ẩn label bảng'
                                  unCheckedChildren='Ẩn label bảng'
                                  onChange={(checked) => {
                                    this.updateWrapField(wi, {
                                      hideLabelInTable: checked,
                                    });
                                  }}
                                />
                              </Col>
                              {wf?.enumable ? (
                                <>
                                  <Col xs={24} sm={12}>
                                    <Select
                                      style={{ width: '100%' }}
                                      value={wf?.enumDisplayAs || 'badge'}
                                      onChange={(v) => {
                                        this.updateWrapField(wi, {
                                          enumDisplayAs: v,
                                        });
                                      }}
                                    >
                                      <Select.Option value='badge'>
                                        Badge / text
                                      </Select.Option>
                                      <Select.Option value='tag'>
                                        Tag màu
                                      </Select.Option>
                                    </Select>
                                  </Col>
                                  <Col xs={24}>
                                    <ArrayEditor
                                      value={wf?.items || []}
                                      onChange={(val: any) => {
                                        this.updateWrapField(wi, {
                                          items: val,
                                        });
                                      }}
                                    />
                                  </Col>
                                </>
                              ) : null}
                              {wf?.modelSelect ? (
                                <Col xs={24} sm={16}>
                                  <Select
                                    style={{ width: '100%' }}
                                    placeholder='Chọn API modelSelect'
                                    value={wf?.modelSelectApi || ''}
                                    onChange={(v) => {
                                      this.updateWrapField(wi, {
                                        modelSelectApi: v,
                                      });
                                    }}
                                  >
                                    <Select.Option value=''>
                                      Chưa chọn
                                    </Select.Option>
                                    {(this.props.apis || []).map((d: any) => (
                                      <Select.Option
                                        key={d.name}
                                        value={d.name}
                                      >
                                        {d.name}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Col>
                              ) : null}
                            </Row>
                          </div>
                        );
                      })}
                      <Button
                        type='dashed'
                        size='small'
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const next = [
                            ...(currentItem[GRID_EDITOR_FIELD.WRAP_FIELDS] ||
                              []),
                            {
                              field: '',
                              type: 'string',
                              display: 'text',
                            },
                          ];
                          this.onItemDataChanged(
                            GRID_EDITOR_FIELD.WRAP_FIELDS,
                            next
                          );
                        }}
                      >
                        Thêm field
                      </Button>
                    </Col>
                  </Row>
                </>
              ) : null}
              {!isWrapEnabled ? (
                <Row {...rowStyles} className='gx-mt-2' align='middle'>
                  <Col {...labelCols}>
                    <label>Kiểu dữ liệu</label>
                  </Col>
                  <Col {...inputCols}>
                    <Row
                      style={{ marginLeft: 0, marginRight: 0 }}
                      justify='space-between'
                      align='middle'
                    >
                      <Col md={12}>
                        <Select
                          style={{ minWidth: '150px' }}
                          value={currentItem.type || 'string'}
                          onChange={(e) => {
                            const displays = this.chooseDisplays(
                              e as DATA_TYPE
                            );
                            const dt = this.state.data.splice(0);
                            const idx = this.state.currentIndex;
                            dt[idx] = {
                              ...dt[idx],
                              [GRID_EDITOR_FIELD.TYPE]: e,
                              [GRID_EDITOR_FIELD.DISPLAY]:
                                displays[0] || undefined,
                            };
                            this.onChange(dt);
                          }}
                        >
                          <Select.Option key={-1} value={''}>
                            Chưa chọn
                          </Select.Option>
                          {DATA_TYPES.map((d, index) => (
                            <Select.Option key={index} value={d}>
                              {d}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col md={12}>
                        {this.renderDisplay({
                          type: currentItem[GRID_EDITOR_FIELD.TYPE],
                          currentItem: currentItem,
                          labelCols,
                        })}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              ) : null}
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Độ rộng</label>
                </Col>
                <Col {...inputCols}>
                  <Input
                    value={currentItem.width || ''}
                    type='number'
                    required
                    onChange={(e) => {
                      this.onItemDataChanged(
                        GRID_EDITOR_FIELD.WIDTH,
                        e.target.value
                      );
                    }}
                  />
                </Col>
              </Row>
              {!isWrapEnabled ? (
                <>
                  <Row {...rowStyles} className='gx-mt-2' align='middle'>
                    <Col {...labelCols}>
                      <label>Danh sách có sẵn</label>
                    </Col>
                    <Col {...inputCols}>
                      <Widgets.CheckboxWidget
                        checkedChildren='Hoạt động'
                        unCheckedChildren='Vô hiệu hoá'
                        value={
                          typeof currentItem.enumable == 'boolean'
                            ? currentItem.enumable
                            : false
                        }
                        onChange={(val: any) => {
                          this.onItemDataChanged(GRID_EDITOR_FIELD.ENUMABLE, val);
                        }}
                      />
                    </Col>
                  </Row>
                  {currentItem.enumable ? (
                    <>
                      <Row {...rowStyles} className='gx-mt-2' align='middle'>
                        <Col {...labelCols}>
                          <label>Kiểu hiển thị</label>
                        </Col>
                        <Col {...inputCols}>
                          <Widgets.RadioGroupWidget
                            valueEnum={{
                              badge: { text: 'Badge', status: 'Custom' },
                              tag: { text: 'Tag + màu', status: 'Custom' },
                            }}
                            value={
                              currentItem[GRID_EDITOR_FIELD.ENUM_DISPLAY_AS] ||
                              'badge'
                            }
                            onChange={(val: any) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.ENUM_DISPLAY_AS,
                                val
                              );
                            }}
                          />
                        </Col>
                      </Row>
                      <Row {...rowStyles} className='gx-mt-2' align='middle'>
                        <Col {...labelCols}>
                          <label>Các lựa chọn</label>
                        </Col>
                        <Col {...inputCols}>
                          <ArrayEditor
                            value={currentItem.items || []}
                            onChange={(val) => {
                              this.onItemDataChanged(GRID_EDITOR_FIELD.ITEMS, val);
                            }}
                          />
                        </Col>
                      </Row>
                    </>
                  ) : null}
                  <Row {...rowStyles} className='gx-mt-2' align='middle'>
                    <Col {...labelCols}>
                      {/* <label>Danh sách chọn csdl</label> */}
                    </Col>
                    <Col {...inputCols}>
                      <Row gutter={[16, 0]}>
                        <Col>
                          <Widgets.CheckboxWidget
                            checkedChildren='Danh sách từ CSDL'
                            unCheckedChildren='Danh sách từ CSDL'
                            value={
                              typeof currentItem.modelSelect == 'boolean'
                                ? currentItem.modelSelect
                                : false
                            }
                            onChange={(val: any) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.MODE_SELECT,
                                val
                              );
                            }}
                          />
                        </Col>
                        {currentItem.modelSelect ? (
                          <Col>
                            <Select
                              style={{ minWidth: '150px' }}
                              value={currentItem.modelSelectApi || ''}
                              onChange={(e) => {
                                this.onItemDataChanged(
                                  GRID_EDITOR_FIELD.MODE_SELECT_API,
                                  e
                                );
                              }}
                            >
                              <Select.Option key={-1} value={''}>
                                Chưa chọn
                              </Select.Option>
                              {this.props.apis.map((d: any) => (
                                <Select.Option key={d.name} value={d.name}>
                                  {d.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Col>
                        ) : null}
                      </Row>
                    </Col>
                  </Row>
                  <Row {...rowStyles} className='gx-mt-2' align='middle'>
                    <Col {...labelCols}>{/* <label>Cho phép lọc</label> */}</Col>
                    <Col {...inputCols}>
                      <Row gutter={[16, 0]}>
                        <Col>
                          <Widgets.CheckboxWidget
                            checkedChildren='Lọc trên cột'
                            unCheckedChildren='Lọc trên cột'
                            value={
                              typeof currentItem.filters == 'boolean'
                                ? currentItem.filters
                                : false
                            }
                            onChange={(val: any) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.FILTERS,
                                val
                              );
                            }}
                          />
                        </Col>
                        <Col>
                          <Widgets.CheckboxWidget
                            checkedChildren='Tìm kiếm trên form'
                            unCheckedChildren='Tìm kiếm trên form'
                            value={
                              typeof currentItem.filterable === 'boolean'
                                ? currentItem.filterable
                                : false
                            }
                            onChange={(val: any) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.FILTERABLE,
                                val
                              );
                            }}
                          />
                        </Col>
                        {currentItem.filterable ? (
                          <Col>
                            <Widgets.CheckboxWidget
                              checkedChildren='Lọc theo khoảng'
                              unCheckedChildren='Lọc theo khoảng'
                              value={
                                typeof currentItem.filterRange == 'boolean'
                                  ? currentItem.filterRange
                                  : false
                              }
                              onChange={(val: any) => {
                                this.onItemDataChanged(
                                  GRID_EDITOR_FIELD.FILTER_RANGE,
                                  val
                                );
                              }}
                            />
                          </Col>
                        ) : null}
                      </Row>
                    </Col>
                  </Row>
                </>
              ) : null}
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Freeze cột</label>
                </Col>
                <Col {...inputCols}>
                  <Widgets.RadioGroupWidget
                    valueEnum={{
                      none: { text: 'Không', status: 'Custom' },
                      left: { text: 'Trái', status: 'Custom' },
                      right: { text: 'Phải', status: 'Custom' },
                    }}
                    value={currentItem[GRID_EDITOR_FIELD.FIXED] || 'none'}
                    onChange={(val: any) => {
                      this.onItemDataChanged(GRID_EDITOR_FIELD.FIXED, val);
                    }}
                  />
                </Col>
              </Row>
              <Row {...rowStyles} className='gx-mt-2' align='middle'>
                <Col {...labelCols}>
                  <label>Thiết lập khác</label>
                </Col>
                <Col {...inputCols}>
                  <Row gutter={[16, 16]} align='middle'>
                    {!isWrapEnabled ? (
                      <Col>
                        <Widgets.CheckboxWidget
                          checkedChildren='Nhúng xem chi tiết'
                          unCheckedChildren='Nhúng xem chi tiết'
                          value={
                            typeof currentItem.viewDetail == 'boolean'
                              ? currentItem.viewDetail
                              : false
                          }
                          onChange={(val: any) => {
                            this.onItemDataChanged(
                              GRID_EDITOR_FIELD.VIEW_DETAIL,
                              val
                            );
                          }}
                        />
                      </Col>
                    ) : null}
                    <Col>
                      <Widgets.CheckboxWidget
                        checkedChildren='Sắp xếp'
                        unCheckedChildren='Sắp xếp'
                        value={
                          typeof currentItem.sorter == 'boolean'
                            ? currentItem.sorter
                            : true
                        }
                        onChange={(val: any) => {
                          this.onItemDataChanged(GRID_EDITOR_FIELD.SORTER, val);
                        }}
                      />
                    </Col>
                    <Col>
                      <Widgets.CheckboxWidget
                        checkedChildren='Ẩn vào setting table'
                        unCheckedChildren='Ẩn vào setting table'
                        value={
                          typeof currentItem[
                            GRID_EDITOR_FIELD.HIDE_IN_SETTING
                          ] == 'boolean'
                            ? currentItem[GRID_EDITOR_FIELD.HIDE_IN_SETTING]
                            : false
                        }
                        onChange={(val: any) => {
                          this.onItemDataChanged(
                            GRID_EDITOR_FIELD.HIDE_IN_SETTING,
                            val
                          );
                        }}
                      />
                    </Col>
                    <Col>
                      <Widgets.CheckboxWidget
                        checkedChildren='Không hiện trên table'
                        unCheckedChildren='Không hiện trên table'
                        value={
                          typeof currentItem[GRID_EDITOR_FIELD.HIDE_IN_TABLE] ==
                            'boolean'
                            ? currentItem[GRID_EDITOR_FIELD.HIDE_IN_TABLE]
                            : false
                        }
                        onChange={(val: any) => {
                          this.onItemDataChanged(
                            GRID_EDITOR_FIELD.HIDE_IN_TABLE,
                            val
                          );
                        }}
                      />
                    </Col>
                    {!isWrapEnabled ? (
                      <>
                        <Col>
                          <Widgets.CheckboxWidget
                            checkedChildren='Nút bấm thay thế'
                            unCheckedChildren='Nút bấm thay thế'
                            value={
                              typeof currentItem.bindButton == 'boolean'
                                ? currentItem.bindButton
                                : false
                            }
                            onChange={(val: any) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.BIND_BUTTON,
                                val
                              );
                            }}
                          />
                        </Col>
                        <Col>
                          <Widgets.CheckboxWidget
                            checkedChildren='Nút copy'
                            unCheckedChildren='Nút copy'
                            value={
                              typeof currentItem.copyable == 'boolean'
                                ? currentItem.copyable
                                : false
                            }
                            onChange={(val: any) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.COPYABLE,
                                val
                              );
                            }}
                          />
                        </Col>
                      </>
                    ) : null}
                    <Col>
                      <Widgets.CheckboxWidget
                        checkedChildren='Dạng ellipsis'
                        unCheckedChildren='Dạng ellipsis'
                        value={
                          typeof currentItem.ellipsis == 'boolean'
                            ? currentItem.ellipsis
                            : false
                        }
                        onChange={(val: any) => {
                          this.onItemDataChanged(
                            GRID_EDITOR_FIELD.ELLIPSIS,
                            val
                          );
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              {!isWrapEnabled ? (
                <Row {...rowStyles} className='gx-mt-2' align='middle'>
                  <Col {...labelCols}>
                    <label>Prefix / Suffix</label>
                  </Col>
                  <Col {...inputCols}>
                    <Row gutter={[16, 8]}>
                      <Col md={12} sm={24}>
                        <Input
                          allowClear
                          placeholder='Prefix cố định, vd: #'
                          value={currentItem[GRID_EDITOR_FIELD.PREFIX] || ''}
                          onChange={(e) => {
                            this.onItemDataChanged(
                              GRID_EDITOR_FIELD.PREFIX,
                              e.target.value
                            );
                          }}
                          addonAfter={
                            <ColorPicker
                              type='sketch'
                              small
                              color={
                                currentItem[GRID_EDITOR_FIELD.PREFIX_COLOR] ||
                                '#595959'
                              }
                              position='top'
                              presetColors={[
                                '#389e0d',
                                '#cf1322',
                                '#1890ff',
                                '#fa8c16',
                                '#722ed1',
                                '#595959',
                              ]}
                              onChangeComplete={(color: string) => {
                                this.onItemDataChanged(
                                  GRID_EDITOR_FIELD.PREFIX_COLOR,
                                  color
                                );
                              }}
                            >
                              {currentItem[GRID_EDITOR_FIELD.PREFIX_COLOR]
                                ? 'Màu'
                                : 'Màu'}
                            </ColorPicker>
                          }
                        />
                      </Col>
                      <Col md={12} sm={24}>
                        <Input
                          allowClear
                          placeholder='Suffix cố định, vd: điểm'
                          value={currentItem[GRID_EDITOR_FIELD.SUFFIX] || ''}
                          onChange={(e) => {
                            this.onItemDataChanged(
                              GRID_EDITOR_FIELD.SUFFIX,
                              e.target.value
                            );
                          }}
                          addonAfter={
                            <ColorPicker
                              type='sketch'
                              small
                              color={
                                currentItem[GRID_EDITOR_FIELD.SUFFIX_COLOR] ||
                                '#595959'
                              }
                              position='top'
                              presetColors={[
                                '#389e0d',
                                '#cf1322',
                                '#1890ff',
                                '#fa8c16',
                                '#722ed1',
                                '#595959',
                              ]}
                              onChangeComplete={(color: string) => {
                                this.onItemDataChanged(
                                  GRID_EDITOR_FIELD.SUFFIX_COLOR,
                                  color
                                );
                              }}
                            >
                              Màu
                            </ColorPicker>
                          }
                        />
                      </Col>
                      <Col md={12} sm={24}>
                        <label style={{ fontSize: 12 }}>
                          Prefix theo cột (vd: direction)
                        </label>
                        <Input
                          allowClear
                          placeholder='Tên field phụ thuộc'
                          value={
                            currentItem[GRID_EDITOR_FIELD.PREFIX_BY_FIELD] || ''
                          }
                          onChange={(e) => {
                            this.onItemDataChanged(
                              GRID_EDITOR_FIELD.PREFIX_BY_FIELD,
                              e.target.value
                            );
                          }}
                        />
                      </Col>
                      <Col md={12} sm={24}>
                        <label style={{ fontSize: 12 }}>
                          Suffix theo cột (vd: direction)
                        </label>
                        <Input
                          allowClear
                          placeholder='Tên field phụ thuộc'
                          value={
                            currentItem[GRID_EDITOR_FIELD.SUFFIX_BY_FIELD] || ''
                          }
                          onChange={(e) => {
                            this.onItemDataChanged(
                              GRID_EDITOR_FIELD.SUFFIX_BY_FIELD,
                              e.target.value
                            );
                          }}
                        />
                      </Col>
                      {currentItem[GRID_EDITOR_FIELD.PREFIX_BY_FIELD] ? (
                        <Col span={24}>
                          <label style={{ fontSize: 12 }}>
                            Map prefix (value → text + màu)
                          </label>
                          <AffixMapEditor
                            value={
                              currentItem[GRID_EDITOR_FIELD.PREFIX_ITEMS] || []
                            }
                            onChange={(val) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.PREFIX_ITEMS,
                                val
                              );
                            }}
                          />
                        </Col>
                      ) : null}
                      {currentItem[GRID_EDITOR_FIELD.SUFFIX_BY_FIELD] ? (
                        <Col span={24}>
                          <label style={{ fontSize: 12 }}>
                            Map suffix (value → text + màu)
                          </label>
                          <AffixMapEditor
                            value={
                              currentItem[GRID_EDITOR_FIELD.SUFFIX_ITEMS] || []
                            }
                            onChange={(val) => {
                              this.onItemDataChanged(
                                GRID_EDITOR_FIELD.SUFFIX_ITEMS,
                                val
                              );
                            }}
                          />
                        </Col>
                      ) : null}
                    </Row>
                  </Col>
                </Row>
              ) : null}
              {!isWrapEnabled && currentItem.viewDetail && (
                <Row {...rowStyles} className='gx-mt-2' align='middle'>
                  <Col {...labelCols}>
                    <label>Giao diện chi tiết</label>
                  </Col>
                  <Col {...inputCols}>
                    <Select
                      style={{ width: '100%' }}
                      value={currentItem.viewDetailCtrl || DEFAULT_VIEW_DETAIL}
                      onChange={(val: any) => {
                        this.onItemDataChanged(
                          GRID_EDITOR_FIELD.VIEW_DETAIL_CTRL,
                          val
                        );
                      }}
                    >
                      {Object.keys(detailTemplateArray).map((temp) => (
                        <Select.Option key={temp} value={temp}>
                          {temp}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    const currentItem = this.state.data[this.state.currentIndex];
    return (
      <>
        <div className='gx-main-content'>
          <div className='gx-editor-module'>
            <div className='gx-editor-sidenav gx-d-lg-flex'>
              {this.OrderableListSideBar()}
            </div>
            <div className='gx-editor-box'>
              <div className='gx-editor-box-header'>
                <div className='gx-editor-box-header-info'>
                  <h5 className='gx-text-uppercase gx-font-weight-bold'>
                    Table
                  </h5>
                  <div className='gx-font-weight-semi-bold'>
                    Quản lý hiển thị bảng dữ liệu
                  </div>
                </div>
                <div className='gx-editor-box-header-setting'>
                  <Tooltip title='Thiết lập Grid'>
                    <SettingOutlined onClick={this.toogleSetting} />
                  </Tooltip>
                </div>
              </div>
              <div className='gx-editor-box-content'>
                {currentItem ? this.Editor(currentItem) : null}
              </div>
            </div>
          </div>
        </div>
        <GridSetting
          name={this.props.name}
          drawerVisible={this.state.visibleSetting}
          onClose={() => {
            this.toogleSetting();
          }}
          settings={this.props.settings}
          onChange={this.onSettingChange}
        />
      </>
    );
  }
}

export default GridEditor;
